import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type OptionType = {
  label: string;
  value: string;
};

export type MultiSelectInputProps = {
  values?: string[];
  subLabel?: string;
  disabled?: boolean;
  options: OptionType[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  selectAllLabel?: string;
  baseHOCClassName?: string;
  className?: string;
};

const SELECT_ALL_VALUE = "__select_all__";

export default function MultiSelectInput({
  values = [],
  onChange,
  options,
  disabled,
  placeholder,
  selectAllLabel,
  className
}: MultiSelectInputProps) {
  const [open, setOpen] = useState(false);
  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const isAllExplicitlySelected =
    allValues.length > 0 && allValues.every((value) => values.includes(value));

  const menuOptions = useMemo(() => {
    if (!selectAllLabel) return options;
    return [{ label: selectAllLabel, value: SELECT_ALL_VALUE }, ...options];
  }, [options, selectAllLabel]);

  const selectedLabels = useMemo(() => {
    if (selectAllLabel && isAllExplicitlySelected) {
      return [selectAllLabel];
    }
    const set = new Set(values);
    return options.filter((o) => set.has(o.value)).map((o) => o.label);
  }, [options, values, selectAllLabel, isAllExplicitlySelected]);

  const toggleValue = (value: string) => {
    if (value === SELECT_ALL_VALUE) {
      onChange(isAllExplicitlySelected ? [] : allValues);
      return;
    }

    const exists = values.includes(value);
    const next = exists ? values.filter((v) => v !== value) : [...values, value];
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          `subtext-small flex h-[40px] w-fit cursor-pointer items-center justify-between overflow-hidden rounded border border-gray-dark-200
            bg-gray-dark-600 px-3 py-2 text-left text-gray-dark-2200 focus:outline-0 disabled:cursor-not-allowed disabled:opacity-50
            lg:max-w-[350px] ${className}`
        )}
        disabled={disabled}
        asChild={false}
      >
        <div className="flex h-[40px] w-full items-center justify-between">
          <div className="flex flex-1 flex-row items-center justify-between">
            <span className="mr-2 truncate">
              {selectedLabels.length > 0 ? `${placeholder}: ` : placeholder}
            </span>
            <span className="truncate">
              {selectedLabels.length > 0 ? selectedLabels.join(", ") : ""}
            </span>
          </div>
          <div className="ml-3 flex h-[40px] min-w-6 items-center justify-center border-l-[1px] bg-gray-dark-600 pl-[10px]">
            {open ? (
              <ChevronUp className="color-gray-dark-2200 h-4 w-4" />
            ) : (
              <ChevronDown className="color-gray-dark-2200 h-4 w-4" />
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="max-h-[240px] w-full overflow-auto p-2">
          {menuOptions.map((item) => {
            const selected =
              item.value === SELECT_ALL_VALUE
                ? isAllExplicitlySelected
                : values.includes(item.value);
            return (
              <button
                key={`multi_select_input_${item.value}`}
                type="button"
                className={`rounded-sm subtext-small relative flex w-full cursor-pointer select-none items-center px-2 py-1.5 outline-none ${
                  selected
                    ? "bg-accent text-accent-foreground"
                    : "text-foreground hover:bg-container dark:hover:bg-gray-dark-700"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleValue(item.value);
                }}
              >
                <span className="flex-1 text-left">{item.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
